import {
    create,
} from "zustand";
import {
    persist,
} from "zustand/middleware";

type ViewMode = "grid" | "list";
export type SortBy = "id" | "media_type" | "views" | "votes" | "tags";
export type SortOrder = "asc" | "desc";
export type ThumbnailSize = "small" | "medium" | "large";

export const SORT_BY_VALUES: SortBy[] = [
    "id",
    "media_type",
    "views",
    "votes",
    "tags",
];
export const SORT_ORDER_VALUES: SortOrder[] = ["asc", "desc"];

type ManageState = {
    viewMode: ViewMode;
    showDeleted: boolean;
    thumbnailSize: ThumbnailSize;
    sortBy: SortBy;
    sortOrder: SortOrder;
    tagQuery: string;
    page: number;
    perPage: number;
    selectedIds: Set<string>;
    setViewMode: (mode: ViewMode) => void;
    setShowDeleted: (show: boolean) => void;
    setThumbnailSize: (size: ThumbnailSize) => void;
    setSort: (by: SortBy, order?: SortOrder) => void;
    toggleSortOrder: () => void;
    setTagQuery: (query: string) => void;
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
    toggleSelection: (id: string) => void;
    selectAll: (ids: string[]) => void;
    clearSelection: () => void;
    isSelected: (id: string) => boolean;
};

export const useManageStore = create<ManageState>()(
    persist(
        (set, get) => ({
            viewMode: "grid",
            showDeleted: false,
            thumbnailSize: "medium",
            sortBy: "id",
            sortOrder: "asc",
            tagQuery: "",
            page: 1,
            perPage: 50,
            selectedIds: new Set<string>(),

            setViewMode: (mode) =>
                set({
                    viewMode: mode,
                }),

            setThumbnailSize: (size) =>
                set({
                    thumbnailSize: size,
                }),

            setShowDeleted: (show) =>
                set({
                    showDeleted: show,
                    page: 1,
                    selectedIds: new Set(),
                }),

            setSort: (by, order) =>
                set((state) => ({
                    sortBy: by,
                    sortOrder: order ?? (state.sortBy === by
                        ? state.sortOrder === "asc" ? "desc" : "asc"
                        : "asc"),
                    page: 1,
                })),

            toggleSortOrder: () =>
                set((state) => ({
                    sortOrder: state.sortOrder === "asc" ? "desc" : "asc",
                })),

            setTagQuery: (query) =>
                set({
                    tagQuery: query,
                    page: 1,
                }),

            setPage: (page) =>
                set({
                    page,
                }),

            setPerPage: (perPage) =>
                set({
                    perPage,
                    page: 1,
                }),

            toggleSelection: (id) =>
                set((state) => {
                    const newSelected = new Set(state.selectedIds);
                    if (newSelected.has(id)) {
                        newSelected.delete(id);
                    } else {
                        newSelected.add(id);
                    }
                    return {
                        selectedIds: newSelected,
                    };
                }),

            selectAll: (ids) =>
                set((state) => {
                    const allSelected = ids.every((id) =>
                        state.selectedIds.has(id)
                    );
                    if (allSelected) {
                        // Deselect all
                        const newSelected = new Set(state.selectedIds);
                        ids.forEach((id) => newSelected.delete(id));
                        return {
                            selectedIds: newSelected,
                        };
                    } else {
                        // Select all
                        const newSelected = new Set(state.selectedIds);
                        ids.forEach((id) => newSelected.add(id));
                        return {
                            selectedIds: newSelected,
                        };
                    }
                }),

            clearSelection: () =>
                set({
                    selectedIds: new Set(),
                }),

            isSelected: (id) => get().selectedIds.has(id),
        }),
        {
            name: "manage-store",
            partialize: (state) => ({
                viewMode: state.viewMode,
                perPage: state.perPage,
                thumbnailSize: state.thumbnailSize,
            }),
        },
    ),
);
